import * as React from 'react';
import CheckOutlinedIcon from '@material-ui/icons/CheckOutlined';
import CloseOutlinedIcon from '@material-ui/icons/CloseOutlined';
import StarIcon from '@material-ui/icons/Star';
import { ApplicationType } from '../../types/enums';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  styled,
  Typography
} from '@material-ui/core';
import { checkFoundation } from '../../helpers/common';
import { get, isNumber } from 'lodash';
import { ItemValue, Model, QuestionRadiogroupModel } from 'survey-core';
import { Loading } from '@pec/aion-ui-components/components/Loading';
import { SubscriptionTiers } from '@pec/aion-ui-core/enums/Subscription';
import { SurveyQuestionElementBase } from 'survey-react-ui';
import i18next from 'i18next';

export const TYPE = 'mui-selectable-subscriptions';
export const CLOSE = 'Close';
export const CHECK = 'Check';
export const SELECTED_PRODUCT = 'survey:orderPageSelectedProduct_';

const productSortingOrder = [
  SubscriptionTiers.Standard,
  SubscriptionTiers.Advanced,
  SubscriptionTiers.VerifiedContractor,
  SubscriptionTiers.VerifiedSupplier,
  SubscriptionTiers.Elite
];

type ProductDetails = {
  description: {
    text: string;
    icon: string;
  }[];
  isMostPopular?: boolean;
};

export const TestLocators = {
  price: 'price'
};

export class MuiSelectableSubscriptionModel extends QuestionRadiogroupModel {
  getType(): string {
    return TYPE;
  }

  get orderByOriginalItem() {
    return this.getPropertyValue('orderByOriginalItem');
  }
  set orderByOriginalItem(value) {
    this.setPropertyValue('orderByOriginalItem', value);
  }

  get filterChoicesOnRender() {
    return this.getPropertyValue('filterChoicesOnRender');
  }
  set filterChoicesOnRender(value) {
    this.setPropertyValue('filterChoicesOnRender', value);
  }

  get price() {
    return this.getPropertyValue('price');
  }
  set price(value) {
    this.setPropertyValue('price', value);
  }

  get productDetails() {
    return this.getPropertyValue('productDetails');
  }
  set productDetails(value) {
    this.setPropertyValue('productDetails', value);
  }

  get productIcons() {
    return this.getPropertyValue('productIcons');
  }
  set productIcons(value) {
    this.setPropertyValue('productIcons', value);
  }

  get strings() {
    return this.getPropertyValue('strings');
  }
  set strings(val) {
    this.setPropertyValue('strings', val);
  }
}

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'var(--white)',
  border: '1px solid var(--on-surface-outline-000000-12-)',
  '&:hover': {
    backgroundColor: 'var(--light-blue-background)',
    cursor: 'pointer'
  },
  '&.selected': {
    backgroundColor: 'var(--light-blue-background)',
    border: '1px solid var(--primary-blue-six)'
  },
  '& .MuiCardHeader-root': {
    paddingLeft: `${theme.spacing(3)}px`,
    paddingRight: `${theme.spacing(3)}px`,
    paddingBottom: `${theme.spacing(1)}px`,
    '& .MuiTypography-root .MuiTypography-body2': {
      fontSize: '0.75rem',
      lineHeight: '1rem',
      letterSpacing: '0.025em',
      color: 'var(--black)'
    },
    '& .MuiTypography-root.MuiTypography-h5': {
      color: 'var(--black)'
    }
  },
  '& .MuiCardHeader-avatar': {
    width: '72px'
  },
  '& .MuiCardContent-root': {
    padding: `${theme.spacing(1)}px ${theme.spacing(3)}px`
  },
  '& .MuiListItem-root': {
    padding: 0,
    alignItems: 'start',
    '& .MuiListItemIcon-root': {
      minWidth: '30px'
    },
    '& .MuiListItemText-root': {
      margin: '0px',
      '&.excluded-option .MuiTypography-caption': {
        color: 'var(--light-gray)'
      }
    }
  },
  '& .MuiCardActions-root': {
    marginTop: 'auto',
    justifyContent: 'center',
    padding: `${theme.spacing(0.5)}px ${theme.spacing(3)}px ${theme.spacing(2)}px`
  },
  '& .MuiCardActions-root .MuiButton-root': {
    border: '1px solid var(--on-surface-outline-000000-12-)',
    lineHeight: 'var(--line-height-16)',
    letterSpacing: 'var(--character-spacing-1-25)',
    padding: `${theme.spacing(1.2)}px ${theme.spacing(1)}px`,
    '&:disabled': {
      backgroundColor: 'var(--disabled)',
      borderColor: 'var(--disabled)'
    }
  }
}));

export class MuiSelectableSubscriptions extends SurveyQuestionElementBase {
  constructor(props: any) {
    super(props);
    this.handleChange = this.handleChange.bind(this);
    this.state = { displayAllPackages: false };
  }

  get question() {
    return this.questionBase;
  }

  get choices() {
    return this.question.visibleChoices;
  }

  get orderByOriginalItem() {
    return this.question.orderByOriginalItem;
  }

  get filterChoicesOnRender() {
    return this.question.filterChoicesOnRender;
  }

  get price() {
    return this.question.price;
  }

  get productDetails() {
    return this.question.productDetails;
  }

  get productIcons() {
    return this.question.productIcons;
  }

  getString(key: string) {
    return get(this.question, ['strings', key], key);
  }

  handleClick(value: string, text: string) {
    this.question.value = value;
    const model = this.question.survey as Model;
    const { organizationId, operationType } = model.getVariable('info');

    const key = `${SELECTED_PRODUCT}${organizationId}_${operationType}`;
    sessionStorage.setItem(
      key,
      JSON.stringify({
        itemName: text,
        itemSrc: this.getProductIcon(text)
      })
    );
  }

  handleChange({ target: { value } }: React.ChangeEvent<HTMLInputElement>) {
    this.question.value = value;
  }

  getChoicesForRender(choices: ItemValue[]) {
    const { survey } = this.question;
    const filteredChoices = choices.filter((item: ItemValue) => {
      if (this.filterChoicesOnRender) {
        const condition = this.filterChoicesOnRender.replace(/\{item}/g, `'${item.value}'`);
        return (survey as Model).runCondition(condition);
      }
      return true;
    });
    this.updateFilteredProducts(filteredChoices);

    const sortedChoices = filteredChoices
      .filter(choice => productSortingOrder.includes(choice.originalItem.subscriptionTier))
      .sort(function(a, b) {
        return (
          productSortingOrder.indexOf(a.originalItem.subscriptionTier) -
          productSortingOrder.indexOf(b.originalItem.subscriptionTier)
        );
      });

    const distinctChoices = filteredChoices.filter(o => sortedChoices.indexOf(o) === -1);

    distinctChoices.sort((a: ItemValue, b: ItemValue) => {
      if (this.orderByOriginalItem) {
        const [field, order] = this.orderByOriginalItem.split(':');
        if (isNumber(a.originalItem[field])) {
          return order === 'desc'
            ? b.originalItem[field] - a.originalItem[field]
            : a.originalItem[field] - b.originalItem[field];
        }
        return order === 'desc'
          ? b.originalItem[field].localeCompare(a.originalItem[field])
          : a.originalItem[field].localeCompare(b.originalItem[field]);
      }
      return 0;
    });

    return sortedChoices.concat(distinctChoices);
  }

  getPrice(value: string) {
    const { survey } = this.question;
    const joiningFeeProductId = (survey as Model).runExpression("getJoiningFee('id')");

    if (this.price) {
      const expression = this.question.price
        .replace(/\{value}/g, `'${value}'`)
        .replace(/\{joiningFeeProductId}/g, `'${joiningFeeProductId}'`);

      return survey.runExpression(expression);
    }
    return null;
  }

  getProductDetails(value: string): ProductDetails | undefined {
    const { survey } = this.question;
    let result;

    if (this.question.productDetails) {
      this.question.productDetails.forEach((element: any) => {
        if (survey.runExpression(element.visibleIf.replace(/\{value}/g, `'${value}'`))) {
          result = element as ProductDetails;
        }
      });
    }
    return result;
  }

  getProductIcon(value: string): string {
    const { survey } = this.question;
    let expression: string = '';
    let result: string = '';

    if (this.question.productIcons) {
      this.question.productIcons.forEach((element: any) => {
        expression = element.visibleIf.replace(/\{subscriptionTier}/g, `'${value}'`);
        if (survey.runExpression(expression)) {
          result = element.iconSrc;
        }
      });
      return result;
    }
    return result;
  }

  getStyledProductExplanation(description: { text: string; icon: string }) {
    switch (description.icon) {
      case CHECK:
        return (
          <>
            <ListItemIcon>
              <CheckOutlinedIcon htmlColor="var(--green)" fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              <Typography variant="caption">{description.text}</Typography>
            </ListItemText>
          </>
        );
      case CLOSE:
        return (
          <>
            <ListItemIcon>
              <CloseOutlinedIcon htmlColor="var(--status-error)" fontSize="small" />
            </ListItemIcon>
            <ListItemText className="excluded-option">
              <Typography variant="caption">{description.text}</Typography>
            </ListItemText>
          </>
        );
      default:
        return <ListItemText>{description.text}</ListItemText>;
    }
  }

  // MuiDiscount uses filtered choices values list to verify code
  updateFilteredProducts(filteredChoices: ItemValue[]) {
    const { survey } = this.question;

    const filteredProducts = (survey as Model).getVariable('filteredProducts') || {};
    (survey as Model).setVariable('filteredProducts', {
      ...filteredProducts,
      [this.question.name]: filteredChoices.map((item: ItemValue) => item.value)
    });
  }

  public componentDidMount(): void {
    this.checkSubscription();
  }

  public componentDidUpdate(prevProps: any): void {
    if (prevProps.survey !== this.props.survey) {
      this.checkSubscription();
    }
  }

  private checkSubscription(): void {
    const { survey } = this.question;
    const model = survey as Model;
    const { currentSubscription } = model.getVariable('info');
    const applicationType = model.data['applicationType'];
    const isFoundation = checkFoundation(currentSubscription);

    if (
      isFoundation ||
      (currentSubscription !== SubscriptionTiers.Elite && currentSubscription !== SubscriptionTiers.Advanced) ||
      applicationType === ApplicationType.Care
    ) {
      this.setState({ displayAllPackages: true });
    }
  }

  private updatePackageViewState(param: boolean): void {
    this.setState({ displayAllPackages: param });
  }

  protected renderElement(): JSX.Element | null {
    const { survey } = this.question;
    const selectedValue = this.question.value ?? '';
    const { location } = (survey as Model).getVariable('info');
    const taxRate = (survey as Model).getVariable('taxRate');
    const joiningFeeProductId = (survey as Model).runExpression("getJoiningFee('id')");
    const model = this.question.survey as Model;
    const { currentSubscription } = model.getVariable('info');
    let isUpgrade = false;

    if (window.location.href.includes('upgrade')) isUpgrade = true;

    if (this.choices.length === 0) {
      return <Loading />;
    }

    let choices: ItemValue[] = this.getChoicesForRender(this.choices);
    if (!this.state.displayAllPackages) choices = choices.filter(subTier => subTier.title === 'CHAS Elite');

    return (
      <div>
        <Grid
          style={{ marginTop: '8px', marginBottom: '4px', justifyContent: 'center' }}
          container
          spacing={3}
          wrap="nowrap"
        >
          {choices.map(({ value, text }: ItemValue) => (
            <Grid item xs={choices.length === 1 ? 5 : 4} key={value} data-packagetype={location}>
              <Box display="flex" justifyContent="center" alignItems="center" height={32} mt={-4}>
                {this.getProductDetails(text)?.isMostPopular && (
                  <>
                    <StarIcon htmlColor="var(--yellow)" />
                    <Typography style={{ fontStyle: 'italic', lineHeight: '1.25rem' }} variant="caption">
                      {this.getString('mostPopular')}
                    </Typography>
                  </>
                )}
              </Box>
              <StyledCard
                variant="outlined"
                className={`${value === selectedValue ? 'selected' : ''}`}
                onClick={() => {
                  this.handleClick(value, text);
                }}
              >
                <CardHeader
                  avatar={<img alt={text} src={`${process.env.PUBLIC_URL}${this.getProductIcon(text)}`} />}
                  title={<Typography variant="h5">{text.replace('CHAS', '')}</Typography>}
                  subheader={
                    <Typography variant="h5" data-testid={TestLocators.price}>
                      {this.getPrice(value)}
                      <Typography component={'span'} variant="body2">
                        {taxRate?.isActive || joiningFeeProductId
                          ? this.getString('year')
                          : this.getString('yearNoTax')}
                      </Typography>
                    </Typography>
                  }
                />
                <CardContent>
                  <List disablePadding>
                    {this.getProductDetails(text)?.description.map((description, index) => (
                      <ListItem key={index}>{this.getStyledProductExplanation(description)}</ListItem>
                    ))}
                  </List>
                </CardContent>
                <CardActions>
                  <Button
                    disabled={value === selectedValue}
                    variant="outlined"
                    fullWidth
                    color="primary"
                    onClick={event => {
                      this.handleClick(value, text);
                      event.stopPropagation();
                    }}
                  >
                    {value === selectedValue ? this.getString('selected') : this.getString('select')}
                  </Button>
                </CardActions>
              </StyledCard>
            </Grid>
          ))}
        </Grid>
        <div style={{ margin: 'auto' }}>
          {currentSubscription === SubscriptionTiers.Advanced && !this.state.displayAllPackages && !isUpgrade && (
            <div>
              <Button onClick={() => this.updatePackageViewState(true)}>
                <Typography
                  style={{ fontStyle: 'italic', lineHeight: '1rem', textTransform: 'lowercase' }}
                  variant="body2"
                >
                  {i18next.t('renew.renewAtCurrent', 'Click here to renew at your current membership level')}
                </Typography>
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }
}
